import { useCallback, useEffect, useMemo, useState } from "react";
import { Toaster } from "sonner";
import {
  AudioGuide,
  LanguageSelector,
  PinVerificationDialog,
  PlatformProvider,
  ReadabilityToggle,
  SelfServicePage,
  audioEmitter,
  setBridgeUrl,
  setSolToken,
  type PracticeSettings,
  type StylingSettings,
} from "@ascleo/frontdesk-shared";
import {
  BrandingProvider,
  brandingAuthorityFingerprint,
  makeBrandingHttpTransport,
  makeBrandingRuntime,
  makeBrowserBrandingResourceLoader,
  type BrandingRuntime,
} from "@ascleo/frontdesk-shared/branding";
import { BrowserSettingsPage } from "./components/BrowserSettingsPage";
import { DemoOperatorPage } from "./components/DemoOperatorPage";
import { OnboardingE2EHarness } from "./components/OnboardingE2EHarness";
import {
  createBrowserPlatformAdapter,
  createDemoPlatformAdapter,
  getSameOriginGatewayUrl,
} from "./lib/browser-platform-adapter";
import { resolveBrowserAppRoute } from "./lib/app-route";
import { frontdeskWebDiagnosticLogger } from "./lib/diagnostic-logger";
import {
  ensureDemoSession,
  getGatewaySession,
  getHostedReadiness,
  type DemoSession,
  type GatewaySession,
  type HostedReadiness,
} from "./lib/gateway-client";
import { evaluateBrowserSetup } from "./lib/setup-readiness";
import { makeWebBrandingCache } from "./lib/web-branding-cache";

type View = "loading" | "settings" | "kiosk";
type BootState = "loading" | "ready" | "error";

interface LiveBrandingSession {
  readonly deviceId: string;
  readonly runtime: BrandingRuntime;
}

async function createLiveBrandingSession(
  deviceId: string,
): Promise<LiveBrandingSession> {
  const authority = await brandingAuthorityFingerprint(deviceId);
  const runtime = makeBrandingRuntime({
    transport: makeBrandingHttpTransport({
      endpoint: new URL("/api/frontdesk/branding/", window.location.origin),
      credentials: "include",
    }),
    cache: makeWebBrandingCache(authority),
    resources: makeBrowserBrandingResourceLoader(),
    observer: {
      report: (event) => {
        frontdeskWebDiagnosticLogger.info("[Branding] lifecycle", {
          kind: event.kind,
          ...(event.kind === "activated" || event.kind === "cache-used" || event.kind === "update-seen"
            ? { version: event.version }
            : {}),
          ...(event.kind === "stage-failed" ? { category: event.category } : {}),
        });
      },
    },
  });
  await runtime.bootstrap();
  return { deviceId, runtime };
}

const emptySession: GatewaySession = {
  enrolled: false,
  ready: false,
  revoked: false,
  expired: false,
  deviceId: null,
  practiceName: null,
  deviceName: null,
  insuiteRestUrl: null,
  token: {
    configured: false,
    maskedSuffix: null,
    fingerprint: null,
    lastVerifiedAt: null,
    lastVerificationResult: "unknown",
  },
  validationIssues: ["missing_gateway_session"],
};

/**
 * Browser app shell for the hosted FrontDesk+ PWA.
 *
 * @since 0.0.0
 * @example
 * ```tsx
 * <App />
 * ```
 */
export function App() {
  const pathname = window.location.pathname;
  const route = useMemo(() => resolveBrowserAppRoute(pathname), [pathname]);

  // E2E-only wizard harness (explicit path; no effect on live/demo routing).
  if (pathname === "/onboarding-e2e") {
    return (
      <>
        <OnboardingE2EHarness />
        <Toaster position="top-center" richColors />
      </>
    );
  }

  return (
    <>
      {route.kind === "live" ? (
        <LiveBrowserApp />
      ) : (
        <DemoBrowserApp operator={route.operator} />
      )}
      <Toaster position="top-center" richColors />
    </>
  );
}

function LiveBrowserApp() {
  const adapter = useMemo(() => createBrowserPlatformAdapter(), []);
  const [bootState, setBootState] = useState<BootState>("loading");
  const [bootError, setBootError] = useState<string | null>(null);
  const [view, setView] = useState<View>("loading");
  const [settingsUnlocked, setSettingsUnlocked] = useState(false);
  const [isPinDialogOpen, setIsPinDialogOpen] = useState(false);
  const [session, setSession] = useState<GatewaySession>(emptySession);
  const [hostedReadiness, setHostedReadiness] =
    useState<HostedReadiness | null>(null);
  const [practiceSettings, setPracticeSettings] =
    useState<PracticeSettings | null>(null);
  const [stylingSettings, setStylingSettings] =
    useState<StylingSettings | null>(null);
  const [brandingSession, setBrandingSession] =
    useState<LiveBrandingSession | null>(null);

  const refreshHostedReadiness = useCallback(
    async (
      nextSession: GatewaySession,
      nextPractice: PracticeSettings,
    ): Promise<HostedReadiness | null> => {
      if (
        !nextSession.enrolled
        || !nextSession.token.configured
        || !nextSession.insuiteRestUrl
      ) {
        setHostedReadiness(null);
        return null;
      }

      try {
        // TEMP(web-only exhibition workaround): remove this polling path after
        // decouple-frontdesk-verification-from-ti-status lands.
        const readiness = await getHostedReadiness(
          nextPractice.selectedTerminalId ?? null,
        );
        setHostedReadiness(readiness);
        return readiness;
      } catch (error) {
        frontdeskWebDiagnosticLogger.error("Failed to load hosted FrontDesk readiness", error);
        const fallback: HostedReadiness = {
          generalApiReachable: false,
          telematikAvailable: false,
          selectedCardReaderAvailable: false,
          hasMedicalCard: false,
          failureReason: "bridge_connectivity_unavailable",
        };
        setHostedReadiness(fallback);
        return fallback;
      }
    },
    [],
  );

  const initialize = useCallback(async () => {
    setBootState("loading");
    setBootError(null);

    try {
      const [practice, styling, gatewaySession] = await Promise.all([
        adapter.getPracticeSettings(),
        adapter.getStylingSettings(),
        getGatewaySession(),
      ]);

      const shellError = configureShell(styling, "live");
      if (shellError) {
        setBootError(shellError);
        setBootState("error");
        return;
      }

      const nextHostedReadiness = await refreshHostedReadiness(
        gatewaySession,
        practice,
      );
      const nextBrandingSession =
        gatewaySession.ready &&
        !gatewaySession.revoked &&
        !gatewaySession.expired &&
        gatewaySession.deviceId
          ? await createLiveBrandingSession(gatewaySession.deviceId)
          : null;

      setSession(gatewaySession);
      setHostedReadiness(nextHostedReadiness);
      setPracticeSettings(practice);
      setStylingSettings(styling);
      setBrandingSession((current) => {
        if (current?.runtime !== nextBrandingSession?.runtime) {
          current?.runtime.stop();
        }
        return nextBrandingSession;
      });
      const nextView = evaluateBrowserSetup(
        gatewaySession,
        practice,
        nextHostedReadiness,
      ).ready
        ? "kiosk"
        : "settings";
      setView(nextView);
      setSettingsUnlocked(false);
      setIsPinDialogOpen(nextView === "settings");
      setBootState("ready");
    } catch (error) {
      frontdeskWebDiagnosticLogger.error("Failed to bootstrap FrontDesk+ live mode", error);
      // Patient-facing copy: German, and no infrastructure vocabulary. The
      // patient can act on "ask at reception"; "same-origin gateway" is for
      // the diagnostic log above, which already has the real error.
      setBootError(
        "Bitte wenden Sie sich an den Empfang. Das Terminal konnte keine Verbindung zur Praxis herstellen.",
      );
      setBootState("error");
    }
  }, [adapter, refreshHostedReadiness]);

  useEffect(function runInitializeEffect() {
    void initialize();
  }, [initialize]);

  const brandingDeviceId =
    session.ready && !session.revoked && !session.expired
      ? session.deviceId
      : null;

  useEffect(function synchronizeBrandingAuthority() {
    if (bootState !== "ready") return;
    if (brandingDeviceId === null) {
      setBrandingSession((current) => {
        current?.runtime.stop();
        return null;
      });
      return;
    }
    if (brandingSession?.deviceId === brandingDeviceId) return;
    setBrandingSession((current) => {
      current?.runtime.stop();
      return null;
    });
    let cancelled = false;
    const initializeBrandingAuthority = async () => {
      try {
        const next = await createLiveBrandingSession(brandingDeviceId);
        if (cancelled) {
          next.runtime.stop();
          return;
        }
        setBrandingSession(next);
      } catch (error) {
        frontdeskWebDiagnosticLogger.warn(
          "Hosted branding cache bootstrap failed",
          error,
        );
        if (!cancelled) {
          setBootError("FrontDesk+ could not initialize its managed branding cache.");
          setBootState("error");
        }
      }
    };
    void initializeBrandingAuthority();
    return function cancelBrandingAuthorityChange() {
      cancelled = true;
    };
  }, [bootState, brandingDeviceId, brandingSession?.deviceId]);

  useEffect(function stopBrandingRuntimeOnUnmount() {
    return function stopBrandingRuntime() {
      brandingSession?.runtime.stop();
    };
  }, [brandingSession]);

  useEffect(function syncInterval() {
    if (
      bootState !== "ready"
      || !practiceSettings
      || !session.enrolled
      || !session.token.configured
      || !session.insuiteRestUrl
    ) {
      return;
    }

    void refreshHostedReadiness(session, practiceSettings);
    const interval = window.setInterval(() => {
      void refreshHostedReadiness(session, practiceSettings);
    }, 30_000);

    return function clearIntervalTimer() {
      window.clearInterval(interval);
    };
  }, [bootState, practiceSettings, refreshHostedReadiness, session]);

  const openLockedSettings = () => {
    setSettingsUnlocked(false);
    setIsPinDialogOpen(true);
    setView("settings");
  };

  const handlePinVerified = () => {
    setSettingsUnlocked(true);
    setIsPinDialogOpen(false);
  };

  const handlePinDialogChange = (open: boolean) => {
    setIsPinDialogOpen(open);
  };

  const handleOpenKiosk = () => {
    setSettingsUnlocked(false);
    setIsPinDialogOpen(false);
    setView("kiosk");
  };

  if (bootState === "error") {
    return (
      <div className="frontdesk-web-shell flex items-center justify-center px-6">
        <ErrorState
          title="FrontDesk+ ist gerade nicht erreichbar."
          description={
            bootError ??
            "Bitte wenden Sie sich an den Empfang. Das Terminal konnte keine Verbindung zur Praxis herstellen."
          }
          actionLabel="Erneut verbinden"
          onAction={() => void initialize()}
          testId="frontdesk-web-bootstrap-error"
        />
      </div>
    );
  }

  if (bootState === "loading" || view === "loading" || !practiceSettings || !stylingSettings) {
    return (
      <div className="frontdesk-web-shell flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (
    view === "kiosk" &&
    brandingDeviceId !== null &&
    brandingSession?.deviceId !== brandingDeviceId
  ) {
    return (
      <div className="frontdesk-web-shell flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  const application = (
    <PlatformProvider adapter={adapter}>
      <div className="frontdesk-web-shell">
        {view === "settings" ? (
          <BrowserSettingsPage
            adapter={adapter}
            session={session}
            hostedReadiness={hostedReadiness}
            practiceSettings={practiceSettings}
            stylingSettings={stylingSettings}
            locked={!settingsUnlocked}
            onSessionChange={setSession}
            onPracticeChange={setPracticeSettings}
            onStylingChange={setStylingSettings}
            onRequestUnlock={openLockedSettings}
            onRefreshHostedReadiness={() =>
              refreshHostedReadiness(session, practiceSettings)
            }
            onOpenKiosk={handleOpenKiosk}
          />
        ) : (
          <div className="frontdesk-web-kiosk-shell">
            <div className="frontdesk-web-kiosk-content">
              <SelfServicePage
                onNavigateToSettings={openLockedSettings}
                requirePinVerificationForSettings={false}
                readinessOverride={
                  hostedReadiness
                    ? {
                        generalApiReachable:
                          hostedReadiness.generalApiReachable,
                        telematikAvailable:
                          hostedReadiness.telematikAvailable,
                        selectedCardReaderAvailable:
                          hostedReadiness.selectedCardReaderAvailable,
                        hasMedicalCard: hostedReadiness.hasMedicalCard,
                      }
                    : null
                }
              />
            </div>
          </div>
        )}
        {view === "settings" ? (
          <PinVerificationDialog
            open={isPinDialogOpen}
            onOpenChange={handlePinDialogChange}
            onVerified={handlePinVerified}
            verifyPin={adapter.verifyPin}
          />
        ) : null}
      </div>
    </PlatformProvider>
  );

  return brandingSession ? (
    <BrandingProvider runtime={brandingSession.runtime}>
      {application}
    </BrandingProvider>
  ) : application;
}

function DemoBrowserApp({ operator }: { readonly operator: boolean }) {
  const adapter = useMemo(() => createDemoPlatformAdapter(), []);
  const [bootState, setBootState] = useState<BootState>("loading");
  const [session, setSession] = useState<DemoSession | null>(null);
  const [stylingSettings, setStylingSettings] =
    useState<StylingSettings | null>(null);

  useEffect(function runInitializeEffect() {
    let cancelled = false;

    const initialize = async () => {
      try {
        const [styling, gatewaySession] = await Promise.all([
          adapter.getStylingSettings(),
          ensureDemoSession(),
        ]);

        configureShell(styling, "demo");

        if (cancelled) {
          return;
        }

        setStylingSettings(styling);
        setSession(gatewaySession);
        setBootState("ready");
      } catch (error) {
        frontdeskWebDiagnosticLogger.error("Failed to bootstrap FrontDesk+ demo mode", error);
        if (!cancelled) {
          setBootState("error");
        }
      }
    };

    void initialize();

    return function cancelInitialize() {
      cancelled = true;
    };
  }, [adapter]);

  if (bootState === "error") {
    return (
      <div className="frontdesk-web-shell flex items-center justify-center px-6">
        <ErrorState
          title={
            operator
              ? "Demo operator mode could not be started."
              : "Demo mode could not be started."
          }
        />
      </div>
    );
  }

  if (bootState === "loading" || !stylingSettings || !session) {
    return (
      <div className="frontdesk-web-shell flex items-center justify-center">
        <Loader
          message={
            operator
              ? "Loading FrontDesk+ demo operator..."
              : "Loading FrontDesk+ demo..."
          }
        />
      </div>
    );
  }

  const defaultLanguage =
    stylingSettings.defaultKioskLanguage || stylingSettings.language || "de";

  return (
    <PlatformProvider adapter={adapter}>
      {operator ? (
        <DemoOperatorPage
          session={session}
          defaultLanguage={defaultLanguage}
          onSessionChange={setSession}
          onReturnToDemo={returnToDemoStart}
        />
      ) : (
        <div className="frontdesk-web-kiosk-shell">
          <div className="frontdesk-web-kiosk-content">
            <SelfServicePage
              requirePinVerificationForSettings={false}
              headerActions={<DemoHeaderActions />}
            />
          </div>
        </div>
      )}
    </PlatformProvider>
  );
}

function DemoHeaderActions() {
  return (
    <>
      <ReadabilityToggle />
      <AudioGuide />
      <LanguageSelector />
    </>
  );
}

function configureShell(
  styling: StylingSettings,
  mode: "live" | "demo",
): string | null {
  let gatewayConfigurationError: string | null = null;

  try {
    setBridgeUrl(getSameOriginGatewayUrl(mode));
  } catch (error) {
    frontdeskWebDiagnosticLogger.error("Failed to configure same-origin FrontDesk gateway", error);
    gatewayConfigurationError =
      "Bitte wenden Sie sich an den Empfang. Dieses Terminal ist noch nicht für die Praxis eingerichtet.";
  }

  setSolToken(null);
  audioEmitter.emit("audio:set-volume", styling.voiceOverVolume ?? 50);

  return gatewayConfigurationError;
}

function returnToDemoStart(): void {
  window.location.replace("/demo");
}

function Loader({ message = "Loading FrontDesk+..." }: { readonly message?: string }) {
  return (
    <div className="rounded-md border bg-white px-5 py-4 text-sm font-medium text-slate-600 shadow-sm">
      {message}
    </div>
  );
}

function ErrorState({
  title,
  description = "Reload the page to start a fresh same-origin demo session.",
  actionLabel = "Reload",
  onAction = () => window.location.reload(),
  testId,
}: {
  readonly title: string
  readonly description?: string
  readonly actionLabel?: string
  readonly onAction?: () => void
  readonly testId?: string
}) {
  return (
    <div
      className="max-w-md rounded-3xl border bg-white/95 p-8 text-center shadow-xl backdrop-blur"
      data-testid={testId}
    >
      <h1 className="text-xl font-semibold text-slate-900">{title}</h1>
      <p className="mt-3 text-sm text-slate-600">
        {description}
      </p>
      <button
        type="button"
        onClick={onAction}
        className="mt-6 inline-flex rounded-full bg-slate-900 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-slate-700"
        data-testid={testId ? `${testId}-action` : undefined}
      >
        {actionLabel}
      </button>
    </div>
  );
}
